import React, { useMemo, useState } from "react";
import { DndContext, DragOverlay, PointerSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { format, isToday } from "date-fns";
import { es } from "date-fns/locale";
import { Lock, Users, Clock, GripVertical, CalendarDays, Sparkles, Check } from "lucide-react";
import { MaterialBadge } from "@/components/MaterialBadge";
import { toast } from "sonner";
import { CheckSeleccion } from "./BarraSeleccion";
import { ResumenOperativo, OjoOperativo } from "./ResumenOperativo";

const WEEKDAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

/** Tarjeta de montaje (línea) del Kanban semanal. Bloqueada si está en ruta o ya montada/cerrada. */
export function MontajeCard({ m, provinciaColor, onClick, overlay, onVer, onCambiarFecha, seleccion }) {
  const bloqueada = !m.reprogramable;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: m.id, disabled: bloqueada, data: { montaje: m } });
  const cls = provinciaColor(m.espacio?.provincia);
  const esp = m.espacio?.nombre || m.cliente_nombre || "—";
  const lugar = [m.espacio?.ciudad, m.espacio?.provincia].filter(Boolean).join(" · ");
  const ref = m.referencia_cliente || m.descripcion;
  const montadores = (m.montadores || []).map((x) => x.nombre_visible).slice(0, 2).join(", ");
  const seleccionada = !!seleccion?.ids?.has(m.id);
  return (
    <div ref={setNodeRef} data-testid={`kanban-card-${m.id}`} data-bloqueada={bloqueada ? "1" : "0"} data-seleccionada={seleccionada ? "1" : "0"}
      className={`rounded border ${cls} px-1.5 py-1 text-[11px] select-none ${overlay ? "shadow-2xl rotate-1" : ""} ${isDragging ? "opacity-30" : ""} ${m.realizado ? "opacity-45" : bloqueada ? "opacity-80" : ""} ${m.sugerido ? "border-dashed border-amber-400" : ""} ${seleccionada ? "ring-2 ring-amber-400 ring-offset-1 ring-offset-zinc-950" : ""}`}>
      <div className="flex items-start gap-1">
        {!overlay && <CheckSeleccion m={m} seleccion={seleccion} size="w-3.5 h-3.5 mt-0.5" />}
        {bloqueada ? (
          <button type="button" className="mt-0.5 shrink-0 text-amber-200" title={m.bloqueo_motivo} data-testid={`kanban-lock-${m.id}`}
            onClick={(e) => { e.stopPropagation(); toast.warning(m.bloqueo_motivo, { duration: 6000 }); }}><Lock className="w-3 h-3" /></button>
        ) : (
          <span {...listeners} {...attributes} className="mt-0.5 shrink-0 cursor-grab active:cursor-grabbing touch-none opacity-60 hover:opacity-100" title="Arrastra para reprogramar" data-testid={`kanban-grip-${m.id}`}><GripVertical className="w-3 h-3" /></span>
        )}
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
          <div className="font-semibold truncate">{m.realizado && <Check className="w-3 h-3 inline mr-0.5 text-emerald-300" />}{esp}</div>
          <div className="font-mono text-[10px] opacity-80 truncate">{m.peticion?.codigo} · {m.peticion?.nombre}</div>
          {lugar && <div className="text-[10px] opacity-70 truncate">{lugar}</div>}
          {ref && <div className="text-[10px] opacity-70 truncate italic">{ref}</div>}
          <ResumenOperativo m={m} />
          <div className="flex items-center gap-1.5 mt-0.5 text-[10px] flex-wrap">
            <MaterialBadge disponibilidad={m.disponibilidad} recepcion={m.recepcion_resumen} componentes={m.componentes} compact testId={`kanban-material-${m.id}`} />
            {m.realizado && <span className="text-emerald-200" data-testid={`kanban-realizado-${m.id}`}>✓ realizado</span>}
            {m.sugerido && <span className="text-amber-200 inline-flex items-center gap-0.5" data-testid={`kanban-sugerido-${m.id}`}><Sparkles className="w-3 h-3" /> sugerido +{m.desvio_km} km</span>}
            {m.fecha_origen === "cita" && <span className="text-cyan-200" data-testid={`kanban-cita-${m.id}`}>Cita / prevista</span>}
            {m.en_ruta && !m.realizado && <span className="text-amber-200 truncate" data-testid={`kanban-enruta-${m.id}`}>🔒 En ruta {m.en_ruta.codigo}{m.en_ruta.fecha ? ` · ${m.en_ruta.fecha.slice(5)}` : ""}</span>}
            {m.espacio?.horario && <span className="inline-flex items-center gap-0.5 opacity-80 truncate max-w-[90px]" title={m.espacio.horario}><Clock className="w-3 h-3" />{m.espacio.horario}</span>}
            {m.requiere_validacion && <span title="Requiere validación">✅?</span>}
            {montadores && <span className="inline-flex items-center gap-0.5 opacity-80"><Users className="w-3 h-3" />{montadores}</span>}
          </div>
        </div>
        {!overlay && (
          <div className="flex flex-col gap-0.5 shrink-0">
            {onVer && <OjoOperativo m={m} onVer={onVer} />}
            {onCambiarFecha && !m.sugerido && !m.realizado && <button type="button" onClick={(e) => { e.stopPropagation(); onCambiarFecha(m); }} className={`p-0.5 rounded hover:bg-zinc-900/60 opacity-70 hover:opacity-100 ${bloqueada ? "opacity-30" : ""}`} title={bloqueada ? m.bloqueo_motivo : "Cambiar fecha (cualquier semana)"} data-testid={`kanban-fecha-${m.id}`}><CalendarDays className="w-3.5 h-3.5" /></button>}
          </div>
        )}
      </div>
    </div>
  );
}

function Columna({ id, titulo, sub, items, hoy, provinciaColor, onCardClick, activo, onVer, onCambiarFecha, seleccion }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} data-testid={`kanban-col-${id}`}
      className={`flex flex-col min-w-[210px] w-[210px] xl:w-auto xl:min-w-0 xl:flex-1 rounded-lg border ${isOver && activo ? "border-amber-400 bg-amber-500/10" : hoy ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-950/40"}`}>
      <div className={`px-2 py-1.5 border-b ${hoy ? "border-amber-500/40" : "border-zinc-800"} flex items-baseline justify-between`}>
        <div><div className="text-[11px] uppercase tracking-wider text-zinc-400">{titulo}</div>{sub && <div className={`text-sm font-bold ${hoy ? "text-amber-300" : "text-zinc-200"}`}>{sub}</div>}</div>
        <span className="text-[10px] text-zinc-500" data-testid={`kanban-count-${id}`}>{items.length}</span>
      </div>
      <div className="p-1.5 space-y-1.5 flex-1 min-h-[300px] max-h-[70vh] overflow-y-auto">
        {items.length === 0 && <div className="text-[10px] text-zinc-700 italic text-center pt-6">{isOver && activo ? "Soltar aquí" : "—"}</div>}
        {items.map((m) => <MontajeCard key={m.id} m={m} provinciaColor={provinciaColor} onClick={() => onCardClick(m)} onVer={onVer} onCambiarFecha={onCambiarFecha} seleccion={seleccion} />)}
      </div>
    </div>
  );
}

/** Planificador semanal: Sin fecha | Lun … Dom. Drag & drop cambia `lineas_trabajo.fecha_programada` (vía onMover). */
export default function KanbanMontajes({ days, montajes, provinciaColor, onCardClick, onMover, onVer, onCambiarFecha, seleccion }) {
  const [active, setActive] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );
  const cols = useMemo(() => {
    const byKey = { "sin-fecha": [] };
    days.forEach((d) => { byKey[format(d, "yyyy-MM-dd")] = []; });
    for (const m of montajes) {
      const fv = m.fecha_visible || m.fecha_programada;
      const k = fv ? fv.slice(0, 10) : "sin-fecha";
      if (byKey[k]) byKey[k].push(m);
    }
    Object.values(byKey).forEach((arr) => arr.sort((a, b) => Number(!!a.realizado) - Number(!!b.realizado) || Number(!!a.sugerido) - Number(!!b.sugerido) || Number(!a.reprogramable) - Number(!b.reprogramable) || (a.espacio?.provincia || "").localeCompare(b.espacio?.provincia || "", "es")));
    return byKey;
  }, [days, montajes]);

  const onDragEnd = async ({ active: a, over }) => {
    setActive(null);
    if (!over) return;
    const m = a.data.current?.montaje;
    const destino = over.id === "sin-fecha" ? null : String(over.id);
    const origen = m?.fecha_visible ? m.fecha_visible.slice(0, 10) : (m?.fecha_programada ? m.fecha_programada.slice(0, 10) : null);
    if (!m || destino === origen) return;
    await onMover(m, destino);
  };

  return (
    <DndContext sensors={sensors} onDragStart={({ active: a }) => setActive(a.data.current?.montaje || null)} onDragEnd={onDragEnd} onDragCancel={() => setActive(null)}>
      <div className="overflow-x-auto pb-2" data-testid="kanban-semana">
        <div className="flex gap-2 min-w-max xl:min-w-0">
          <Columna id="sin-fecha" titulo="Sin fecha" sub="pendientes" items={cols["sin-fecha"]} provinciaColor={provinciaColor} onCardClick={onCardClick} activo={!!active} onVer={onVer} onCambiarFecha={onCambiarFecha} seleccion={seleccion} />
          {days.map((d, i) => {
            const k = format(d, "yyyy-MM-dd");
            return <Columna key={k} id={k} titulo={WEEKDAYS[i]} sub={format(d, "d MMM", { locale: es })} items={cols[k] || []} hoy={isToday(d)} provinciaColor={provinciaColor} onCardClick={onCardClick} activo={!!active} onVer={onVer} onCambiarFecha={onCambiarFecha} seleccion={seleccion} />;
          })}
        </div>
      </div>
      <DragOverlay>{active ? <div className="w-[200px]"><MontajeCard m={active} provinciaColor={provinciaColor} onClick={() => {}} overlay /></div> : null}</DragOverlay>
    </DndContext>
  );
}
